/**
 * Sniper chart-overlay strategy (R129 rewrite) — dual-timeframe CLH contrarian
 * pullback. Mirrors backend strategies/sniperRules.ts VERBATIM so chart markers,
 * the scan and the backtest never diverge.
 *
 *   trend(tf): CLH close > prevClose ⇒ up (+1); < ⇒ down (−1); == ⇒ carry.
 *   Vol spike = isSpike; Vol Bull/Bear spike = spike on an up/down raw candle.
 *   BUY  (ISL = 3-bar low,  Target = entry + N·risk):
 *     B_C1 = HTF up ∧ CTF down ∧ no-upper-wick ∧ Vol spike
 *     B_C2 = HTF up ∧ CTF down ∧ has-lower-wick ∧ Vol Bull spike
 *     B_C3 = HTF up ∧ CTF up   ∧ no-lower-wick ∧ Vol Bull spike
 *   SHORT (ISL = 3-bar high, Target = entry − N·risk):
 *     S_C1 = HTF down ∧ CTF up   ∧ no-lower-wick ∧ Vol spike
 *     S_C2 = HTF down ∧ CTF up   ∧ has-upper-wick ∧ Vol Bear spike
 *     S_C3 = HTF down ∧ CTF down ∧ no-upper-wick ∧ Vol Bear spike
 *   SELL  = first FRESH CTF downtrend after the BUY's down-cycle ends (exit long)
 *   COVER = first FRESH CTF uptrend after the SHORT's up-cycle ends  (exit short)
 *
 * The matched condition (B_C1…S_C3 / SELL / COVER) is the marker text.
 * Colouring is position-state: LONG (BUY→SELL) green, SHORT (SHORT→COVER) red,
 * FLAT neutral. One position per symbol; no auto-flip.
 */
import { CandleRow } from '../../components/charts/TradingChart';
import { ChartStrategy, StrategyOverlayResult, ChartMarker, CandleColor, ChartStrategyOptions } from './types';
import { transformCandles } from '../candleTransforms';
import { computeWVol } from '../wvol';
import { OVERVIEW_ZONE_COLORS } from '../../theme/overviewColors';

export interface SniperConfig { targetRiskMultiple: number; noWickTolerancePct: number; }
export const DEFAULT_SNIPER_CONFIG: SniperConfig = { targetRiskMultiple: 2, noWickTolerancePct: 0.05 };

type Pos = 'LONG' | 'SHORT' | 'FLAT';
type Sig = 'BUY' | 'SHORT' | 'EXIT_LONG' | 'EXIT_SHORT' | null;
type Reason = 'B_C1' | 'B_C2' | 'B_C3' | 'S_C1' | 'S_C2' | 'S_C3' | 'SELL' | 'COVER';

const COLOR_LONG = OVERVIEW_ZONE_COLORS.strongBuy;
const COLOR_SHORT = OVERVIEW_ZONE_COLORS.strongShort;
const COLOR_FLAT = OVERVIEW_ZONE_COLORS.neutral;

function trendSeries(clh: CandleRow[]): number[] {
  const out: number[] = new Array(clh.length).fill(0);
  let state = 0;
  for (let i = 1; i < clh.length; i++) {
    const c = Number(clh[i].close), p = Number(clh[i - 1].close);
    if (Number.isFinite(c) && Number.isFinite(p)) {
      if (c > p) state = 1; else if (c < p) state = -1;
    }
    out[i] = state;
  }
  return out;
}

interface SniperBar {
  time: number; close: number;
  ctfTrend: number; htfTrend: number;
  noUpperWick: boolean; noLowerWick: boolean; volSpike: boolean; volBull: boolean;
  low3: number | null; high3: number | null;
}

interface SniperStep { time: number; signal: Sig; reason: Reason | null; pos: Pos; isl: number | null; target: number | null; }

function computeSniperBars(ctfRaw: CandleRow[], htfRaw: CandleRow[], cfg: SniperConfig): SniperBar[] {
  if (!ctfRaw?.length) return [];
  const ctf = transformCandles(ctfRaw, 'CLH');
  const htf = htfRaw?.length ? transformCandles(htfRaw, 'CLH') : [];
  const ctfTrend = trendSeries(ctf);
  const htfTrend = trendSeries(htf);
  const wv = computeWVol(ctfRaw);
  const tolFrac = Math.max(0, cfg.noWickTolerancePct) / 100;

  const htfByCtf: number[] = new Array(ctf.length).fill(0);
  let cur = -1;
  for (let i = 0; i < ctf.length; i++) {
    const t = Number(ctf[i].time);
    while (cur + 1 < htf.length && Number(htf[cur + 1].time) <= t) cur++;
    htfByCtf[i] = cur >= 0 ? htfTrend[cur] : 0;
  }

  const out: SniperBar[] = new Array(ctf.length);
  for (let i = 0; i < ctf.length; i++) {
    const c = ctf[i];
    const o = Number(c.open), h = Number(c.high), l = Number(c.low), cl = Number(c.close);
    const range = Math.max(1e-9, h - l);
    const tol = range * tolFrac;
    const bodyTop = Math.max(o, cl), bodyBot = Math.min(o, cl);
    let low3: number | null = null, high3: number | null = null;
    if (i >= 3) {
      low3 = Math.min(Number(ctf[i - 1].low), Number(ctf[i - 2].low), Number(ctf[i - 3].low));
      high3 = Math.max(Number(ctf[i - 1].high), Number(ctf[i - 2].high), Number(ctf[i - 3].high));
    }
    out[i] = {
      time: c.time as number, close: cl,
      ctfTrend: ctfTrend[i], htfTrend: htfByCtf[i],
      noUpperWick: (h - bodyTop) <= tol, noLowerWick: (bodyBot - l) <= tol,
      volSpike: !!wv[i]?.isSpike, volBull: !!wv[i]?.isBull, low3, high3,
    };
  }
  return out;
}

function evaluateSniper(bars: SniperBar[], cfg: SniperConfig): SniperStep[] {
  const out: SniperStep[] = [];
  const N = Math.max(0.1, cfg.targetRiskMultiple);
  let pos: Pos = 'FLAT';
  let downCycle = 0, upCycle = 0, prevTrend = 0;
  let buyDownCycle = -1, shortUpCycle = -1;

  for (const b of bars) {
    const t = b.ctfTrend;
    const freshDown = t === -1 && prevTrend !== -1;
    const freshUp = t === 1 && prevTrend !== 1;
    if (freshDown) downCycle++;
    if (freshUp) upCycle++;

    let signal: Sig = null; let reason: Reason | null = null;
    let isl: number | null = null, target: number | null = null;
    const hasLowerWick = !b.noLowerWick, hasUpperWick = !b.noUpperWick;
    const volBullSpike = b.volSpike && b.volBull, volBearSpike = b.volSpike && !b.volBull;
    const riskOkLong = b.low3 != null && b.low3 < b.close;
    const riskOkShort = b.high3 != null && b.high3 > b.close;

    if (pos === 'LONG') {
      if (freshDown && downCycle > buyDownCycle) { signal = 'EXIT_LONG'; reason = 'SELL'; pos = 'FLAT'; }
    } else if (pos === 'SHORT') {
      if (freshUp && upCycle > shortUpCycle) { signal = 'EXIT_SHORT'; reason = 'COVER'; pos = 'FLAT'; }
    } else if (b.htfTrend === 1 && riskOkLong) {
      if (b.ctfTrend === -1 && b.noUpperWick && b.volSpike) reason = 'B_C1';
      else if (b.ctfTrend === -1 && hasLowerWick && volBullSpike) reason = 'B_C2';
      else if (b.ctfTrend === 1 && b.noLowerWick && volBullSpike) reason = 'B_C3';
      if (reason) { signal = 'BUY'; pos = 'LONG'; isl = b.low3; target = b.close + N * (b.close - isl!); buyDownCycle = downCycle; }
    } else if (b.htfTrend === -1 && riskOkShort) {
      if (b.ctfTrend === 1 && b.noLowerWick && b.volSpike) reason = 'S_C1';
      else if (b.ctfTrend === 1 && hasUpperWick && volBearSpike) reason = 'S_C2';
      else if (b.ctfTrend === -1 && b.noUpperWick && volBearSpike) reason = 'S_C3';
      if (reason) { signal = 'SHORT'; pos = 'SHORT'; isl = b.high3; target = b.close - N * (isl! - b.close); shortUpCycle = upCycle; }
    }
    out.push({ time: b.time, signal, reason, pos, isl, target });
    prevTrend = t === 0 ? prevTrend : t;
  }
  return out;
}

/** Build the chart overlay (position-state colours + B/S/X markers). */
export function computeSniperOverlay(ctf: CandleRow[], htf: CandleRow[], cfg: SniperConfig = DEFAULT_SNIPER_CONFIG): StrategyOverlayResult {
  if (!ctf?.length) return { candleColors: [], markers: [] };
  const bars = computeSniperBars(ctf, htf, cfg);
  const steps = evaluateSniper(bars, cfg);
  const candleColors: CandleColor[] = [];
  const markers: ChartMarker[] = [];
  const signalsByTime = new Map<number, { signal: 'BUY' | 'SHORT' | 'EXIT_LONG' | 'EXIT_SHORT'; strength: 'HIGH' | 'MEDIUM' | 'LOW' | null; pos: Pos; reason?: string | null }>();
  const positionByTime = new Map<number, Pos>();

  for (const s of steps) {
    const color = s.pos === 'LONG' ? COLOR_LONG : s.pos === 'SHORT' ? COLOR_SHORT : COLOR_FLAT;
    candleColors.push({ time: s.time, upColor: color, downColor: color });
    positionByTime.set(s.time, s.pos);
    if (!s.signal) continue;
    signalsByTime.set(s.time, { signal: s.signal, strength: null, pos: s.pos, reason: s.reason });
    const tag = s.reason || '';   // B_C1…S_C3 / SELL / COVER
    if (s.signal === 'BUY') markers.push({ time: s.time, position: 'belowBar', text: tag || 'B', color: '#00C853', shape: 'arrowUp', size: 2 });
    else if (s.signal === 'SHORT') markers.push({ time: s.time, position: 'aboveBar', text: tag || 'S', color: '#D50000', shape: 'arrowDown', size: 2 });
    else if (s.signal === 'EXIT_LONG') markers.push({ time: s.time, position: 'aboveBar', text: tag || 'SELL', color: '#ff9800', shape: 'circle' });
    else if (s.signal === 'EXIT_SHORT') markers.push({ time: s.time, position: 'belowBar', text: tag || 'COVER', color: '#ff9800', shape: 'circle' });
  }
  return { candleColors, markers, signalsByTime, positionByTime };
}

export const sniperStrategy: ChartStrategy = {
  id: 'sniper',
  name: 'Sniper',
  computeOverlays(candles: CandleRow[], opts?: ChartStrategyOptions): StrategyOverlayResult {
    const htf = opts?.htfCandles || [];
    const cfg: SniperConfig = {
      targetRiskMultiple: opts?.rockBandThresholds ? DEFAULT_SNIPER_CONFIG.targetRiskMultiple : DEFAULT_SNIPER_CONFIG.targetRiskMultiple,
      noWickTolerancePct: DEFAULT_SNIPER_CONFIG.noWickTolerancePct,
    };
    return computeSniperOverlay(candles, htf, cfg);
  },
};
